import React, { useEffect, useState } from 'react' 
import DatePicker from 'react-datepicker' 
import 'react-datepicker/dist/react-datepicker.css' 
import { FaFilter } from 'react-icons/fa' 
import CustomSelect from './CustomSelect' 
import moment from 'moment' 
import { useDeviceHealthContextData } from '../context/Context' 
import { 
  getCityDepotContract, 
  getLastRefreshDate 
} from '../DeviceHealthService' 
import { getISTDate } from '../../../../../Shared/common'
import styles from './CustomFilter.module.css'
 
const initial = { 
  search: [], 
  city: [], 
  depot: [], 
  contract: [] 
} 
 
const SEARCH_DEBOUNCE_MS = 400

// search is kept as an array of VINs; trims spaces and drops empty entries
const toSearchArray = value =>
  (Array.isArray(value) ? value : String(value || '').split(','))
    .map(s => s.trim())
    .filter(Boolean)

const makeOptions = (data, key) =>
  [...new Set(data.map(x => x[key]).filter(Boolean))].map(x => ({ 
    label: x, 
    value: x 
  })) 
 
export default function CustomFilter({ onApply, onReset }) { 
  const { GlobalFilter, setGlobalFilters } = useDeviceHealthContextData() 
  const [filters, setFilters] = useState(initial) 
  const [showFilter, setShowFilter] = useState(false) 
  const [data, setData] = useState([]) 
  const [options, setOptions] = useState({ city: [], depot: [], contract: [] }) 
  const [lastRefreshDate, setLastRefreshDate] = useState('--') 
 
  // City / Depot / Contract options scoped to the VINs typed in Search
  // (debounced); an empty search loads the full location list.
  const searchKey = toSearchArray(filters.search).join(',')

  useEffect(() => {
    const searchValues = searchKey ? searchKey.split(',') : []
    const body = searchValues.length ? { search: searchValues } : {}

    let cancelled = false

    // no debounce for the full list (first load / search cleared)
    const timeoutId = setTimeout(() => {
      getCityDepotContract(body)
        .then(res => { if (!cancelled) setData(res?.data || []) })
        .catch(err => { if (!cancelled) setData([]) })
    }, searchValues.length ? SEARCH_DEBOUNCE_MS : 0)

    // ignore responses from an older search
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [searchKey])

  useEffect(() => {
    getLastRefreshDate()
      .then(res => { 
        setLastRefreshDate(res?.lastRefreshDate || '--') 
        setFilters(prev => ({ 
          ...prev, 
          startDate: moment(res?.lastRefreshDate, 'YYYY-MM-DD').toDate(), 
          endDate: moment(res?.lastRefreshDate, 'YYYY-MM-DD').toDate() 
        })) 
      }) 
      .catch(err => setLastRefreshDate('--')) 
  }, []) 
 
 
  useEffect(() => { 
    setOptions({ 
      city: makeOptions(data, 'city'), 
      depot: makeOptions(data, 'depot'), 
      contract: makeOptions(data, 'contract') 
    }) 
    setFilters(prev=>({ 
      ...initial, 
      ...prev, 
      city: makeOptions(data, 'city'), 
      depot: makeOptions(data, 'depot'), 
      contract: makeOptions(data, 'contract') 
    })) 
  }, [data]) 
 
 
  const related = (selected, key) => { 
    const values = selected.map(x => x.value) 
 
    if (!values.length) { 
      return { 
        city: [], 
        depot: [], 
        contract: [] 
      } 
    } 
 
    const rows = data.filter(x => values.includes(x[key])) 
 
    return { 
      city: makeOptions(rows, 'city'), 
      depot: makeOptions(rows, 'depot'), 
      contract: makeOptions(rows, 'contract') 
    } 
  } 
 
  const updateFilter = (selected, name) => { 
    const selectedItems = selected || [] 
    const r = related(selectedItems, name) 
 
    setOptions(prev => ({ 
      ...prev, 
      city: name === 'city' ? prev.city : r.city, 
      depot: name === 'depot' ? prev.depot : r.depot, 
      contract: name === 'contract' ? prev.contract : r.contract 
    })) 
 
    setFilters(prev => ({ 
      ...prev, 
      [name]: selectedItems, 
      city: name === 'city' ? selectedItems : r.city, 
      depot: name === 'depot' ? selectedItems : r.depot, 
      contract: name === 'contract' ? selectedItems : r.contract 
    })) 
  } 
 
  const update = (key, value) => setFilters(p => ({ ...p, [key]: value })) 
 
  const submit = e => { 
    e.preventDefault() 

    const updatedFilters = {
      ...filters,
      search: toSearchArray(filters.search),
      startDate: getISTDate(filters.startDate),
      endDate: getISTDate(filters.endDate)
    }

    setGlobalFilters(prev => ({ ...prev, ...updatedFilters })) 
    onApply?.(updatedFilters) 
  } 
 
  const reset = e => { 
    e.preventDefault() 
    const allOptions = { 
      city: makeOptions(data, 'city'), 
      depot: makeOptions(data, 'depot'), 
      contract: makeOptions(data, 'contract') 
    } 
    const f = { 
      ...initial, 
      ...allOptions, 
      startDate: moment(lastRefreshDate, 'YYYY-MM-DD').toDate(), 
      endDate: moment(lastRefreshDate, 'YYYY-MM-DD').toDate(), 
    } 

    setOptions(allOptions) 
    setFilters(f) 

    const resetFilters = {
      ...f,
      startDate: getISTDate(f.startDate),
      endDate: getISTDate(f.endDate),
    }

    setGlobalFilters(prev => ({ 
      ...prev, 
      ...resetFilters,
       "frequency":""
    })) 
 
    onReset?.(resetFilters) 
  } 
 
  return ( 
    <> 
      <div className='container-fluid pt-1 cardBox'> 
        <div className='card'> 
          <div className='card-body busflt'> 
            <div className='row d-flex justify-content-between'> 
              <div className='col-lg-4 col-sm-4 d-flex justify-content-start align-items-center'> 
                <p className='my-auto small'> 
                  Last Refresh Date: {lastRefreshDate} 
                </p> 
              </div> 
              <div className='col-lg-4 col-sm-4  d-none d-md-flex justify-content-center align-items-center'> 
                <h5 className='mb-0 font-bus-head'>TCU</h5> 
              </div> 
              <div className='col-lg-4 col-sm-4 text-end filter_bus_icon_f'> 
                <button 
                  type='button' 
                  onClick={() => setShowFilter(p => !p)} 
                  className='btn btn-sm btn-outline-primary material-icons filter_bus_icon' 
                > 
                  <FaFilter /> 
                </button> 
              </div> 
            </div> 
          </div> 
        </div> 
      </div> 
 
      <div className='container-fluid'> 
        <div
          className={`FilterDiv_bus p-2 mb-3 ${styles.filterPanel} ${showFilter ? styles.filterPanelOpen : ''}`}
          hidden={!showFilter}
        >
          <form className='g-3 mt-2 filterForm' onSubmit={submit}> 
            <div className='row'> 
              <div className='col-lg-2 col-md-6 col-sm-12'> 
                <label className='form-label_bus'>Search</label> 
                <input 
                  value={filters.search} 
                  onChange={e => 
                    update( 
                      'search', 
                      e.target.value ? e.target.value.split(',') : [] 
                    ) 
                  } 
                  className='top-filter filter-col form-control' 
                  placeholder='Search...' 
                  autoComplete='off' 
                /> 
              </div> 
 
              <div className='col-lg-2 col-md-6 col-sm-12' style={{ zIndex: 2 }}> 
                <label className='form-label_bus'>Date Range</label> 
                <DatePicker 
 
                  selectsRange 
                  startDate={filters.startDate} 
                  endDate={filters.endDate} 
                  onChange={([start, end]) => { 
                    update('startDate', start) 
                    update('endDate', end) 
                  }} 
                  // n-1: one month back up to yesterday (today not selectable)
                  minDate={moment().subtract(1, 'month').startOf('day').toDate()}
                  maxDate={moment().subtract(1, 'day').endOf('day').toDate()}
                  dateFormat='dd-MM-yyyy' 
                  placeholderText='Select Start Date - End Date' 
                  className='form-control' 
                  isClearable 
                  wrapperClassName='w-100' 
                /> 
              </div> 
 
              <div className='col-lg-2 col-md-6 col-sm-12'> 
                <CustomSelect 
                  name='city' 
                  options={options.city} 
                  value={filters.city} 
                  isMulti 
                  placeholder='Select City' 
                  onChange={updateFilter} 
                /> 
              </div> 
 
              <div className='col-lg-2 col-md-6 col-sm-12'> 
                <CustomSelect 
                  name='depot' 
                  options={options.depot} 
                  value={filters.depot} 
                  isMulti 
                  placeholder='Select Depot' 
                  onChange={updateFilter} 
                /> 
              </div> 
 
              <div className='col-lg-2 col-md-6 col-sm-12 mb-1'> 
                <CustomSelect 
                  name='contract' 
                  options={options.contract} 
                  value={filters.contract} 
                  isMulti 
                  placeholder='Select Contract' 
                  onChange={updateFilter} 
                /> 
              </div> 
              <div className='col-lg-1 col-md-6 col-sm-12 d-flex align-items-center mt-1'> 
                <button className='fleet-btn fleet-btn-sm blue-theme h-50 custom-Btn-animation mr-1'> 
                  Apply 
                </button> 
                <button 
                  type='button' 
                  onClick={reset} 
                  className='fleet-btn fleet-btn-sm gray-theme  h-50 custom-Btn-animation' 
                > 
                  Reset 
                </button> 
              </div> 
            </div> 
 
 
 
          </form> 
        </div> 
      </div> 
    </> 
  ) 
}